import "utils/errorPolyfill";

import { mode, Mode } from "app";

import React, { ReactNode, useEffect, useState, startTransition } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { firebaseAuth, APP_BASE_PATH } from "app";
import { useAuth } from "utils/AuthContext";
import { MainLayout } from "components/MainLayout";
import { Sidebar } from "components/Sidebar";
import { AuthProvider } from "utils/AuthContext";
import { TopNavBar } from "components/TopNavBar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { BottomNavBar } from "components/BottomNavBar"; // <-- Import
import { useSidebarStore } from "utils/sidebarStore";
import { onAuthStateChanged } from "firebase/auth"; // For profile store
import { useProfileStore } from "utils/useProfileStore"; // For profile store
import { useChatStore } from "utils/chatStore";
import { isConversationUnreadFor } from "utils/chatTypes";
import { clearLazyReloadFlag } from "utils/lazyWithReload";
import { useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY } from "utils/googleMapsConfig";
import AuthErrorBoundary from "components/AuthErrorBoundary";
import { QueryClient, QueryClientProvider } from "react-query";
// Some newer components (e.g. ChangeLogDisplay) use @tanstack/react-query
// v5 hooks. We provide a second QueryClient so those hooks find a client
// without disrupting every existing v3 consumer.
import {
  QueryClient as TanstackQueryClient,
  QueryClientProvider as TanstackQueryClientProvider,
} from "@tanstack/react-query";
import { useVideoCallStore } from "utils/videoCallStore";
import { IncomingCallModal } from "components/IncomingCallModal";
import { VideoCallInterface } from "components/VideoCallInterface";
import { CallParticipant } from "utils/videoCallTypes";
import { ChatSidebar } from 'components/ChatSidebar';
import { ActiveShiftMonitor } from 'components/ActiveShiftMonitor';
import { AppointmentConfirmationDialog } from "components/AppointmentConfirmationDialog";
import { useUserTimezone } from "utils/userTimezone";
import { useAppointmentConfirmationStore } from "utils/appointmentConfirmationStore";
import NotificationBell from 'components/NotificationBell';
import { subscribeToConversations } from 'utils/chatService';
import { toast } from "sonner";
import { useWorkingHoursStore } from "utils/workingHoursStore";
import { useImpersonationStore } from "utils/useImpersonationStore";
import { useFCMInitialization } from "utils/useFCMInitialization";
import { MessageSquare } from "lucide-react";

// Move libraries to module scope to prevent recreation on each render
const GOOGLE_MAPS_LIBRARIES: ("places")[] = ["places"];

// Global flag to track if Google Maps API has been loaded to prevent HMR issues
let isGoogleMapsLoaded = false;

// Check if Google Maps is already available globally
const isGoogleMapsAlreadyAvailable = () => {
  return !!(window.google?.maps?.places?.Autocomplete);
};

interface AppProviderProps {
  children: ReactNode;
}

// Define paths that should NOT use the MainLayout (Sidebar)
// Paths should be lowercase and start with a slash
const NO_LAYOUT_PATHS = ["/login", "/logout", "/signup"]; // Adjust if needed

// Define paths that clients and limited_access users CAN access
const CLIENT_ALLOWED_PATHS = [
  "/",
  "/login",
  "/logout",
  "/client-chat",
  "/clientchat",
  "/my-appointments",
  "/myappointments",
  "/client-schedule-appointment",
  "/clientscheduleappointment",
  "/events",
  "/event-details",
  "/eventdetails",
  "/news",
  "/user-profile",
  "/userprofile",
  "/notification-preferences",
  "/notificationpreferences",
  "/settings",
  "/sign-up",
  "/signup"
];

import { queryClient as tanstackQueryClient } from 'utils/diaryQueryClient';

const queryClient = new QueryClient();

const AppProviderInner: React.FC<AppProviderProps> = ({ children }) => {
  const { user, role, loading: authLoading } = useAuth(); // Use centralized auth with role
  // Load the user's saved timezone into the app-wide active zone so every
  // page renders/enters times in users/{uid}.timezone (default device time).
  useUserTimezone();
  const location = useLocation();
  const navigate = useNavigate();
  const [shouldRenderMainLayout, setShouldRenderMainLayout] = useState(false);
  const { isOpen, setOpen } = useSidebarStore();
  const { conversations, setConversations } = useChatStore();
  
  // Chat sidebar state (persisted in localStorage)
  const [isChatSidebarOpen, setIsChatSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('chatSidebarOpen');
    return saved === 'true';
  });
  const [isChatMinimized, setIsChatMinimized] = useState(() => {
    const saved = localStorage.getItem('chatSidebarMinimized');
    return saved === 'true';
  });
  
  // Initialize FCM for push notifications when user logs in
  useFCMInitialization(user);

  // Once the app has booted past the lazy router, clear the chunk-reload flag
  // so future deploy-mismatch crashes can trigger another single reload.
  useEffect(() => {
    clearLazyReloadFlag();
  }, []);
  
  // Video call store
  const {
    incomingCalls,
    currentCall,
    showCallInterface,
    subscribeToUserCalls,
    acceptIncomingCall,
    declineIncomingCall,
    endCurrentCall
  } = useVideoCallStore();
  
  // Appointment confirmation store
  const {
    isOpen: isConfirmationOpen,
    appointmentId,
    appointmentDetails,
    notificationId,
    closeDialog,
  } = useAppointmentConfirmationStore();

  // Persist chat sidebar state
  useEffect(() => {
    localStorage.setItem('chatSidebarOpen', String(isChatSidebarOpen));
  }, [isChatSidebarOpen]);

  useEffect(() => {
    localStorage.setItem('chatSidebarMinimized', String(isChatMinimized));
  }, [isChatMinimized]);

  const toggleChatSidebar = () => setIsChatSidebarOpen(!isChatSidebarOpen);
  const toggleChatMinimize = () => setIsChatMinimized(!isChatMinimized);
  const closeChatSidebar = () => setIsChatSidebarOpen(false);

  // Check if Google Maps is already loaded to prevent HMR issues
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(() => {
    const alreadyAvailable = isGoogleMapsAlreadyAvailable();
    if (alreadyAvailable && !isGoogleMapsLoaded) {
      isGoogleMapsLoaded = true;
    }
    return alreadyAvailable;
  });
  
  // Always use consistent options for useJsApiLoader to prevent "different options" error
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-maps-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    preventGoogleFontsLoading: true,
  });
  
  // Handle Google Maps loading
  useEffect(() => {
    // Check if already loaded from previous HMR cycle
    if (isGoogleMapsAlreadyAvailable() && !isGoogleMapsLoaded) {
      window.dispatchEvent(new Event('google-maps-api-loaded'));
      isGoogleMapsLoaded = true;
      setIsGoogleMapsReady(true);
      return;
    }
    
    if (isLoaded && !isGoogleMapsLoaded) {
      // Dispatch a custom event when Google Maps is loaded
      window.dispatchEvent(new Event('google-maps-api-loaded'));
      // Set the global flag to true
      isGoogleMapsLoaded = true;
      setIsGoogleMapsReady(true);
    }
    if (loadError) {
      // Google Maps API failed to load
      console.error('❌ Google Maps API failed to load:', loadError);
    }
  }, [isLoaded, loadError]);

  // --- Profile Store Integration ---
  useEffect(() => {
    const { subscribeToProfile, clearProfile } = useProfileStore.getState();
    const { clearAllWorkingHours } = useWorkingHoursStore.getState();

    // Load the EFFECTIVE user's profile: while a super admin impersonates
    // someone, the app-wide profile (role / isSuperAdmin / name) must be
    // the impersonated user's — otherwise super-admin-only UI (approval
    // buttons, all-tasks view, etc.) still renders and impersonation lies.
    const subscribeEffective = () => {
      const authUser = firebaseAuth.currentUser;
      if (!authUser) return;
      const { isImpersonating, impersonatedUser } = useImpersonationStore.getState();
      const effectiveId = isImpersonating && impersonatedUser ? impersonatedUser.id : authUser.uid;
      subscribeToProfile(effectiveId);
    };

    // Listen for authentication state changes from Firebase Auth
    const unsubscribeAuth = onAuthStateChanged(firebaseAuth, (authUser) => {
      if (authUser) {
        subscribeEffective();
      } else {
        // User is signed out according to Firebase Auth, clear profile data from store
        clearProfile();
        // Clear all working hours listeners to prevent permission-denied errors
        clearAllWorkingHours();
      }
    });
    // Re-subscribe whenever impersonation starts/stops
    const unsubscribeImpersonation = useImpersonationStore.subscribe(subscribeEffective);
    // Cleanup function: Unsubscribe from the Firebase Auth listener when AppProvider unmounts
    return () => {
      unsubscribeAuth();
      unsubscribeImpersonation();
    };
  }, []); // Empty dependency array ensures this effect runs only once on mount

  // --- Video Call Listener (Global) ---
  useEffect(() => {
    if (!user) return;
    const unsubscribe = subscribeToUserCalls(user.uid);
    return unsubscribe;
  }, [user]); // Fixed: Removed subscribeToUserCalls from dependencies - Zustand functions are stable

  // --- Global Chat Conversations Subscription ---
  // Previously this lived in `pages/App.tsx`, but App.tsx only mounts on
  // the dashboard route. Visiting any other page (e.g. /diary) tore the
  // subscription down, leaving the ChatSidebar reading an empty store
  // ("No conversations yet"). Moving it into AppProvider — which wraps
  // every authenticated route — keeps the listener alive everywhere.
  useEffect(() => {
    if (!user) return;
    const profile = useProfileStore.getState().profile as any;
    const isAdmin = role === 'admin' || profile?.isSuperAdmin === true;
    const unsubscribe = subscribeToConversations(user.uid, setConversations, isAdmin);
    return () => unsubscribe?.();
  }, [user, role, setConversations]);

  // --- Role-Based Route Protection ---
  useEffect(() => {
    // Only proceed if auth state is resolved
    if (authLoading || !user) {
      return;
    }

    // Normalize the current path
    const basePathPrefix: string = APP_BASE_PATH === "/" || APP_BASE_PATH === "" ? "" : APP_BASE_PATH;
    let normalizedPathname = location.pathname;
    if (basePathPrefix && normalizedPathname.startsWith(basePathPrefix)) {
      normalizedPathname = normalizedPathname.substring(basePathPrefix.length);
    }
    normalizedPathname = (normalizedPathname.startsWith("/") ? normalizedPathname : `/${normalizedPathname}`).toLowerCase();

    // Check if user is client or limited_access
    const isClientOrLimited = role === 'client' || role === 'limited_access';
    
    if (isClientOrLimited) {
      // Check if current path is allowed for clients
      const isAllowedPath = CLIENT_ALLOWED_PATHS.some(allowedPath => 
        normalizedPathname === allowedPath.toLowerCase() ||
        normalizedPathname.startsWith(allowedPath.toLowerCase() + '?') // Handle query params
      );

      if (!isAllowedPath) {
        // Show error toast
        toast.error('Access Denied', {
          description: 'You do not have permission to access this page.',
        });
        
        // Redirect to home
        navigate('/', { replace: true });
      }
    }
  }, [user, role, location.pathname, authLoading, navigate]);

  // --- Layout Determination Logic ---
  useEffect(() => {
    // Only proceed with layout determination if auth state is resolved
    if (authLoading) {
      return;
    }
    
    // Normalize the current path to handle base paths and case differences
    const basePathPrefix: string = APP_BASE_PATH === "/" || APP_BASE_PATH === "" ? "" : APP_BASE_PATH;
    let normalizedPathname = location.pathname;
    if (basePathPrefix && normalizedPathname.startsWith(basePathPrefix)) {
      normalizedPathname = normalizedPathname.substring(basePathPrefix.length);
    }
    normalizedPathname = (normalizedPathname.startsWith("/") ? normalizedPathname : `/${normalizedPathname}`).toLowerCase();
    // Check if the normalized path is in the list of public paths
    const isPublicPath = NO_LAYOUT_PATHS.includes(normalizedPathname);
    // Determine if the main layout should be used: user is logged in AND it's not a public path
    const wantsMainLayout = user && !isPublicPath; // Use 'user' from useCurrentUser hook here

    // Use startTransition to update layout state without blocking UI
    startTransition(() => {
      setShouldRenderMainLayout(wantsMainLayout);
    });
  // Re-run this effect if the authenticated user changes or the URL path changes
  }, [user, location.pathname, authLoading]);

  // --- Loading State ---
  // Show a simple loading message while the initial authentication check is happening
  if (authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        Loading application...
      </div>
    );
  }

  // --- Render Logic ---
  const renderContent = () => (
    <AuthErrorBoundary>
      <TooltipProvider>
        <TanstackQueryClientProvider client={tanstackQueryClient}>
        <QueryClientProvider client={queryClient}>
          {/* Main container ensuring flex column layout and min height */}
          <div className="flex flex-col min-h-screen">
            {/* Top Navigation Bar */}
            <TopNavBar />
            {/* Content area that grows to fill available space */}
            <div className={`flex-grow ${shouldRenderMainLayout ? 'pb-20' : ''}`}>
              {/* Conditionally render MainLayout or a simple wrapper */}
              {shouldRenderMainLayout ? (
                // Apply MainLayout (which includes the main content area and potentially sidebar structure)
                <MainLayout>{children}</MainLayout>
              ) : (
                // For public pages or logged-out users, render children directly with some padding
                <div className="public-page-wrapper min-h-full px-1 py-4 md:px-1 md:py-6">{children}</div>
              )}
            </div>
          </div>
          {/* Always render Bottom Navigation Bar in mobile view */}
          <BottomNavBar />
          {/* Toast notifications provider */}
          <Toaster richColors position="top-right" duration={3000} closeButton={true} />
          
          {/* Chat Sidebar FAB and Component - Inside main layout */}
          {user && shouldRenderMainLayout && (() => {
            // Don't show for clients or limited_access users
            if (role === 'client' || role === 'limited_access') return null;
            
            // Don't show on the chat page itself
            // Normalize the current path (same way it's done elsewhere in this component)
            const basePathPrefix: string = APP_BASE_PATH === "/" || APP_BASE_PATH === "" ? "" : APP_BASE_PATH;
            let normalizedPathname = location.pathname;
            if (basePathPrefix && normalizedPathname.startsWith(basePathPrefix)) {
              normalizedPathname = normalizedPathname.substring(basePathPrefix.length);
            }
            normalizedPathname = (normalizedPathname.startsWith("/") ? normalizedPathname : `/${normalizedPathname}`).toLowerCase();
            
            // Check if we're on any chat-related page
            if (normalizedPathname === '/chat' || 
                normalizedPathname === '/clientchat' || 
                normalizedPathname === '/client-chat' ||
                normalizedPathname.startsWith('/chat/') ||
                normalizedPathname.startsWith('/clientchat/') ||
                normalizedPathname.startsWith('/client-chat/')) {
              return null;
            }
            
            // Don't show on mobile (check window width)
            if (typeof window !== 'undefined' && window.innerWidth < 768) return null;
            
            // Calculate total unread messages count — shared helper
            // so we honour Bug 1 sender guard and Bug 2 admin_client
            // team-acknowledge state.
            const unreadCount = conversations.filter(conversation =>
              isConversationUnreadFor(conversation, user.uid)
            ).length;

            return (
              <>
                {/* Floating Action Button for Chat - Hidden on mobile */}
                <button
                  onClick={toggleChatSidebar}
                  className="hidden md:flex fixed w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 items-center justify-center hover:scale-110"
                  style={{ 
                    position: 'fixed', 
                    bottom: '96px', 
                    right: '24px', 
                    zIndex: 9999,
                    left: 'auto'
                  }}
                  title="Toggle Chat"
                >
                  <MessageSquare className="h-6 w-6" />
                  {/* Unread count badge */}
                  {unreadCount > 0 && (
                    <div className="absolute -top-1 -right-1 h-6 w-6 bg-red-500 border-2 border-white rounded-full flex items-center justify-center text-xs font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </div>
                  )}
                </button>
                
                {/* Chat Sidebar Component - Hidden on mobile */}
                <div className="hidden md:block">
                  <ChatSidebar
                    isOpen={isChatSidebarOpen}
                    onClose={closeChatSidebar}
                    isMinimized={isChatMinimized}
                    onToggleMinimize={toggleChatMinimize}
                  />
                </div>
              </>
            );
          })()}
          {/* Sheet component for the mobile/collapsible sidebar */}
          <Sheet open={isOpen} onOpenChange={setOpen}>
            <SheetContent side="left" className="p-0 w-[280px] sm:max-w-xs">
              {/* Ensure sidebar content is scrollable if it exceeds height */}
              <div className="h-full overflow-y-auto">
                <Sidebar />
              </div>
            </SheetContent>
          </Sheet>
          
          {/* Global Video Call Modals */}
          {user && incomingCalls.length > 0 && (
            <IncomingCallModal
              call={incomingCalls[0]}
              currentUserId={user.uid}
              onAccept={() => acceptIncomingCall(incomingCalls[0].id, user.uid)}
              onDecline={() => declineIncomingCall(incomingCalls[0].id, user.uid)}
            />
          )}
          
          {user && showCallInterface && (() => {
            const { profile } = useProfileStore.getState();
            const currentUserAsCallParticipant: CallParticipant = {
              id: user.uid,
              name: profile?.name || (profile?.firstName ? `${profile.firstName} ${profile.lastName}` : user.displayName || user.email || 'Unknown User'),
              photoURL: profile?.photoURL || user.photoURL || undefined,
              role: (profile?.role as 'admin' | 'staff' | 'client') || 'client',
              isVideoEnabled: true,
              isAudioEnabled: true,
            };
            
            // Show connecting state while waiting for call to be created
            if (!currentCall) {
              return (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
                    <p className="text-lg font-medium">Connecting call...</p>
                  </div>
                </div>
              );
            }
            
            return (
              <VideoCallInterface
                call={currentCall}
                currentUser={currentUserAsCallParticipant}
                onEndCall={() => endCurrentCall(user.uid)}
              />
            );
          })()}
        </QueryClientProvider>
        </TanstackQueryClientProvider>
      </TooltipProvider>
    </AuthErrorBoundary>
  );

  // Return content directly - Google Maps API is loaded via useJsApiLoader hook
  return (
    <>
      <ActiveShiftMonitor />
      
      {/* Global Incoming Call Modal */}
      {incomingCalls.length > 0 && (
        <IncomingCallModal
          call={incomingCalls[0]}
          currentUserId={user.uid}
          onAccept={() => acceptIncomingCall(incomingCalls[0].id, user.uid)}
          onDecline={() => declineIncomingCall(incomingCalls[0].id, user.uid)}
        />
      )}
      
      {/* Global Appointment Confirmation Dialog */}
      {appointmentId && (
        <AppointmentConfirmationDialog
          open={isConfirmationOpen}
          onOpenChange={(open) => {
            if (!open) closeDialog();
          }}
          appointmentId={appointmentId}
          appointmentDetails={appointmentDetails}
          notificationId={notificationId || undefined}
          onConfirmed={() => {
            // Optionally refresh appointments or show success
            closeDialog();
          }}
        />
      )}

      {renderContent()}
    </>
  );
};

// Main AppProvider that wraps everything in AuthProvider
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  return (
    <AuthProvider>
      <AppProviderInner>{children}</AppProviderInner>
    </AuthProvider>
  );
};
